import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plane, 
  Search, 
  Star, 
  Clock, 
  MapPin, 
  Wind, 
  Cloud,
  Sparkles,
  Navigation,
  History,
  Heart,
  Loader2,
  RefreshCw,
  ArrowRight,
  FileText,
  FolderOpen,
  Wrench,
  ChevronDown,
  ThumbsUp,
  HelpCircle,
  Download,
  Smartphone,
  Settings,
  Sun,
  Moon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import WeatherCard from "@/components/WeatherCard";
import RouteWeather from "@/components/RouteWeather";
import FavoritesList from "@/components/FavoritesList";
import HistoryView from "@/components/HistoryView";
import BriefingForm from "@/components/BriefingForm";
import BriefingLibrary from "@/components/BriefingLibrary";
import { useTheme } from "@/context/ThemeContext";
import "./Dashboard.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("form");
  const [metar, setMetar] = useState(null);
  const [taf, setTaf] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [currentIcao, setCurrentIcao] = useState("");
  // Multi-airport support
  const [multiAirportData, setMultiAirportData] = useState([]);
  const [isMultiAirport, setIsMultiAirport] = useState(false);
  // Briefing editing
  const [editingBriefing, setEditingBriefing] = useState(null);
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
  // XDU likes counter - persisted to backend
  const [xduLikes, setXduLikes] = useState(0);
  // AI Help dialog
  const [showAiHelpDialog, setShowAiHelpDialog] = useState(false);
  // Favorite airports dropdown
  const [showFavoriteAirports, setShowFavoriteAirports] = useState(false);
  const searchInputRef = useRef(null);
  
  // PWA Install state
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);

  // PWA Install prompt listener
  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsAppInstalled(isStandalone);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsAppInstalled(true);
      setInstallPrompt(null);
      toast.success('App installed successfully!');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallApp = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('Installing app...');
      }
      setInstallPrompt(null);
    } else {
      // Show manual instructions
      toast.info('To install: tap browser menu (⋮) then select Install app or Add to Home Screen');
    }
  };

  const fetchXduLikes = async () => {
    try {
      const response = await axios.get(`${API}/xdu-likes`);
      setXduLikes(response.data.likes);
    } catch (error) {
      console.error("Failed to fetch XDU likes:", error);
    }
  };

  const handleXduLike = async () => {
    try {
      const response = await axios.post(`${API}/xdu-likes`);
      setXduLikes(response.data.likes);
    } catch (error) {
      console.error("Failed to increment XDU likes:", error);
    }
  };

  useEffect(() => {
    fetchFavorites();
    fetchXduLikes();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      setFavorites(response.data);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };

  const searchWeather = async (icao = searchQuery) => {
    const input = icao.toUpperCase().trim();
    
    // Check if multiple airports (comma-separated)
    const codes = input.split(',').map(c => c.trim()).filter(c => c.length === 4);
    
    if (codes.length === 0) {
      toast.error("Please enter valid 4-character ICAO code(s)");
      return;
    }

    setLoading(true);
    setMetar(null);
    setTaf(null);
    setExplanation(null);
    setMultiAirportData([]);
    
    // Single airport mode
    if (codes.length === 1) {
      setIsMultiAirport(false);
      setCurrentIcao(codes[0]);

      try {
        const [metarRes, tafRes] = await Promise.all([
          axios.get(`${API}/weather/metar/${codes[0]}`).catch(() => null),
          axios.get(`${API}/weather/taf/${codes[0]}`).catch(() => null)
        ]);

        if (metarRes?.data) {
          setMetar(metarRes.data);
        } else {
          toast.error(`No METAR data found for ${codes[0]}`);
        }

        if (tafRes?.data) {
          setTaf(tafRes.data);
        }
      } catch (error) {
        toast.error("Failed to fetch weather data");
      } finally {
        setLoading(false);
      }
    } 
    // Multi-airport mode
    else {
      setIsMultiAirport(true);
      setCurrentIcao(codes.join(', '));

      try {
        const results = await Promise.all(
          codes.map(async (code) => {
            const [metarRes, tafRes] = await Promise.all([
              axios.get(`${API}/weather/metar/${code}`).catch(() => null),
              axios.get(`${API}/weather/taf/${code}`).catch(() => null)
            ]);
            return {
              icao: code,
              metar: metarRes?.data || null,
              taf: tafRes?.data || null
            };
          })
        );

        setMultiAirportData(results);
        
        // Set first airport's data for backward compatibility
        if (results[0]?.metar) setMetar(results[0].metar);
        if (results[0]?.taf) setTaf(results[0].taf);
        
        toast.success(`Fetched weather for ${codes.length} airports`);
      } catch (error) {
        toast.error("Failed to fetch weather data");
      } finally {
        setLoading(false);
      }
    }
  };

  const generateExplanation = async () => {
    if (!metar && !taf) return;

    setExplaining(true);
    try {
      const response = await axios.post(`${API}/weather/explain`, {
        icao: currentIcao,
        metar: metar?.raw,
        taf: taf?.raw
      });
      setExplanation(response.data.explanation);
      toast.success("Weather briefing generated!");
    } catch (error) {
      toast.error("Failed to generate explanation");
    } finally {
      setExplaining(false);
    }
  };

  const toggleFavorite = async (icao) => {
    const code = icao.toUpperCase();
    const isFavorite = favorites.some(f => f.icao === code);

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${code}`);
        toast.success(`Removed ${code} from favorites`);
      } else {
        await axios.post(`${API}/favorites`, { icao: code });
        toast.success(`Added ${code} to favorites`);
      }
      fetchFavorites();
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const getFlightCategoryStyle = (category) => {
    switch (category?.toUpperCase()) {
      case "VFR": return "status-vfr";
      case "MVFR": return "status-mvfr";
      case "IFR": return "status-ifr";
      case "LIFR": return "status-lifr";
      default: return "bg-slate-700 text-slate-300";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      searchWeather();
    }
  };

  return (
    <div className={`dashboard-container ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      {/* Header */}
      <header className="dashboard-header">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_preflight-weather/artifacts/t3bj072m_NSAS-RGB-zilver-baseline.png" 
                alt="NSAS - North Sea Aviation Services" 
                className="dashboard-logo"
                onClick={() => navigate('/')}
                title="Back to Home"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <div className="live-badge">
                <span className="pulse-dot" />
                Live Data
              </div>
              <button
                onClick={toggleTheme}
                className="theme-toggle"
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="settings-btn"
                title="Admin Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Search Section - Hidden on Library tab */}
        {activeTab !== 'library' && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-bold text-slate-700">Use with random airports without FTX designator</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-stretch w-full">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10 pointer-events-none" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Enter ICAO code(s) or click to select favorites"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setShowFavoriteAirports(false);
                      searchWeather();
                    } else if (e.key === "Escape") {
                      setShowFavoriteAirports(false);
                      searchInputRef.current?.blur();
                    }
                  }}
                  onFocus={() => setShowFavoriteAirports(true)}
                  onClick={() => setShowFavoriteAirports(true)}
                  onBlur={(e) => {
                    // Delay closing to allow click on dropdown items
                    setTimeout(() => setShowFavoriteAirports(false), 150);
                  }}
                  className="pl-12 pr-10 search-input w-full uppercase h-12"
                  maxLength={50}
                  data-testid="icao-search-input"
                />
                <Star className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400 pointer-events-none" />
                
                {/* Favorites Dropdown */}
                {showFavoriteAirports && favorites.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden">
                    <div className="p-2 border-b border-slate-100 bg-slate-50">
                      <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-400" />
                        Your Favorites ({favorites.length})
                      </p>
                    </div>
                    <ScrollArea className="max-h-[280px]">
                      <div className="p-1">
                        {favorites.filter(airport => {
                          // Get the parts split by comma
                          const parts = searchQuery.split(',').map(p => p.trim().toUpperCase());
                          
                          // All complete parts (4-char ICAO codes that have been selected)
                          const completeParts = parts.filter(p => p.length === 4);
                          
                          // The current typing part (last part if not complete)
                          const lastPart = parts[parts.length - 1];
                          const isTyping = lastPart.length < 4;
                          
                          // Don't show already selected airports
                          if (completeParts.includes(airport.icao)) return false;
                          
                          // If not typing (full ICAO entered), show all non-selected
                          if (!isTyping) return true;
                          
                          // Filter by what user is typing
                          return !lastPart || 
                            airport.icao.includes(lastPart) || 
                            (airport.name && airport.name.toUpperCase().includes(lastPart));
                        }).map((airport) => (
                          <button
                            key={airport.icao}
                            className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-md hover:bg-blue-50 transition-colors group"
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent blur
                              // If there's already content, append with comma
                              if (searchQuery && !searchQuery.endsWith(',')) {
                                setSearchQuery(prev => `${prev},${airport.icao}`);
                              } else if (searchQuery.endsWith(',')) {
                                setSearchQuery(prev => `${prev}${airport.icao}`);
                              } else {
                                setSearchQuery(airport.icao);
                              }
                              setShowFavoriteAirports(false);
                            }}
                            data-testid={`favorite-airport-${airport.icao}`}
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                              <Star className="w-4 h-4 text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-800">{airport.icao}</p>
                              {airport.name && (
                                <p className="text-xs text-slate-500 truncate">{airport.name}</p>
                              )}
                            </div>
                          </button>
                        ))}
                        {(() => {
                          const parts = searchQuery.split(',').map(p => p.trim().toUpperCase());
                          const completeParts = parts.filter(p => p.length === 4);
                          const lastPart = parts[parts.length - 1];
                          const isTyping = lastPart.length > 0 && lastPart.length < 4;
                          
                          if (!isTyping) return null;
                          
                          const filtered = favorites.filter(airport => 
                            !completeParts.includes(airport.icao) && (
                              airport.icao.includes(lastPart) || 
                              (airport.name && airport.name.toUpperCase().includes(lastPart))
                            )
                          );
                          return filtered.length === 0 ? (
                            <div className="px-3 py-4 text-center text-sm text-slate-500">
                              No matching favorites. Press Enter to search for "{lastPart}"
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </ScrollArea>
                  </div>
                )}
                {showFavoriteAirports && favorites.length === 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden">
                    <div className="p-4 text-center text-sm text-slate-500">
                      <Star className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                      <p>No favorites yet</p>
                      <p className="text-xs mt-1">Search for an airport and click the star to add it</p>
                    </div>
                  </div>
                )}
              </div>
              <Button 
                onClick={() => searchWeather()}
                disabled={loading || searchQuery.trim().length < 4}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 shadow-md hover:shadow-lg transition-shadow whitespace-nowrap"
                data-testid="search-weather-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Search className="w-5 h-5 mr-2" />
                )}
                Get Weather
              </Button>
            </div>
          </div>
        )}

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 border border-slate-200 p-1 gap-1 flex-wrap h-auto justify-start">
            <TabsTrigger 
              value="form" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-600 text-xs sm:text-sm px-2 sm:px-3 py-2"
              data-testid="tab-form"
            >
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Briefing Form</span>
            </TabsTrigger>
            <TabsTrigger 
              value="library" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-600 text-xs sm:text-sm px-2 sm:px-3 py-2"
              data-testid="tab-library"
            >
              <FolderOpen className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Library</span>
            </TabsTrigger>
            {/* Wx Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`text-xs sm:text-sm px-2 sm:px-3 py-2 h-auto ${
                    ['briefing', 'route', 'history', 'favorites'].includes(activeTab)
                      ? 'bg-blue-600 text-white hover:bg-blue-700 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="tab-wx-tools"
                >
                  <Wrench className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Wx Tools</span>
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white">
                <DropdownMenuItem 
                  onClick={() => setActiveTab('briefing')}
                  className={`cursor-pointer ${activeTab === 'briefing' ? 'bg-blue-50 text-blue-700' : ''}`}
                >
                  <Cloud className="w-4 h-4 mr-2" />
                  NSAS TOOLBOX
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setActiveTab('route')}
                  className={`cursor-pointer ${activeTab === 'route' ? 'bg-blue-50 text-blue-700' : ''}`}
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Route Weather
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setActiveTab('history')}
                  className={`cursor-pointer ${activeTab === 'history' ? 'bg-blue-50 text-blue-700' : ''}`}
                >
                  <History className="w-4 h-4 mr-2" />
                  History
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setActiveTab('favorites')}
                  className={`cursor-pointer ${activeTab === 'favorites' ? 'bg-blue-50 text-blue-700' : ''}`}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Favorites
                  {favorites.length > 0 && (
                    <span className="ml-2 bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">
                      {favorites.length}
                    </span>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TabsList>

          {/* Weather Briefing Tab */}
          <TabsContent value="briefing" className="space-y-6" data-testid="briefing-content">
            {!metar && !taf && !loading ? (
              <div className="text-center py-20">
                <div className="inline-flex p-4 rounded-full bg-slate-100 mb-6">
                  <MapPin className="w-12 h-12 text-slate-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-700 mb-2">
                  Enter an ICAO Code to Begin
                </h2>
                <p className="text-slate-500 max-w-md mx-auto">
                  Search for any airport worldwide using its 4-letter ICAO code 
                  to get the latest METAR and TAF weather data.
                  <br /><span className="text-blue-600 font-medium">Use commas to search multiple airports (e.g., EHAM,EBOS,EGLL)</span>
                </p>
              </div>
            ) : isMultiAirport && multiAirportData.length > 0 ? (
              /* Multi-airport view */
              <div className="space-y-6">
                {multiAirportData.map((airport, index) => (
                  <div key={airport.icao} className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                      <div className="p-1.5 rounded bg-blue-100">
                        <Plane className="w-4 h-4 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800">{airport.icao}</h3>
                      {airport.metar?.flight_category && (
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${getFlightCategoryStyle(airport.metar.flight_category)}`}>
                          {airport.metar.flight_category}
                        </span>
                      )}
                    </div>
                    <div className="bento-grid">
                      <div className="col-span-12 lg:col-span-6">
                        <WeatherCard
                          type="METAR"
                          data={airport.metar}
                          loading={loading}
                          onFavorite={() => toggleFavorite(airport.icao)}
                          isFavorite={favorites.some(f => f.icao === airport.icao)}
                          getFlightCategoryStyle={getFlightCategoryStyle}
                        />
                      </div>
                      <div className="col-span-12 lg:col-span-6">
                        <WeatherCard
                          type="TAF"
                          data={airport.taf}
                          loading={loading}
                          getFlightCategoryStyle={getFlightCategoryStyle}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bento-grid">
                {/* METAR Card */}
                <div className="col-span-12 lg:col-span-6">
                  <WeatherCard
                    type="METAR"
                    data={metar}
                    loading={loading}
                    onFavorite={() => currentIcao && toggleFavorite(currentIcao)}
                    isFavorite={favorites.some(f => f.icao === currentIcao)}
                    getFlightCategoryStyle={getFlightCategoryStyle}
                  />
                </div>

                {/* TAF Card */}
                <div className="col-span-12 lg:col-span-6">
                  <WeatherCard
                    type="TAF"
                    data={taf}
                    loading={loading}
                    getFlightCategoryStyle={getFlightCategoryStyle}
                  />
                </div>

                {/* AI Explanation */}
                {(metar || taf) && (
                  <div className="col-span-12">
                    <div className="weather-card p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-purple-100 border border-purple-200">
                            <Sparkles className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-slate-800">
                                AI Weather Briefing
                              </h3>
                              <button
                                onClick={() => setShowAiHelpDialog(true)}
                                className="p-1 rounded-full text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                                title="How is this data generated?"
                              >
                                <HelpCircle className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-xs text-slate-500">
                              Plain English explanation of current conditions
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={generateExplanation}
                          disabled={explaining}
                          variant="outline"
                          className="border-purple-300 text-purple-700 hover:bg-purple-50"
                          data-testid="generate-explanation-btn"
                        >
                          {explaining ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              {explanation ? "Regenerate" : "Generate Briefing"}
                            </>
                          )}
                        </Button>
                      </div>

                      {/* AI Help Dialog */}
                      <Dialog open={showAiHelpDialog} onOpenChange={setShowAiHelpDialog}>
                        <DialogContent className="sm:max-w-[500px]">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              <HelpCircle className="w-5 h-5 text-purple-600" />
                              How is this data generated?
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 text-sm text-slate-700">
                            <div>
                              <h4 className="font-semibold text-slate-800 mb-1">Data Source</h4>
                              <p>The AI explanation is generated by <strong>OpenAI GPT-5.2</strong> via the /api/weather/explain endpoint.</p>
                            </div>
                            
                            <div>
                              <h4 className="font-semibold text-slate-800 mb-1">Input Data</h4>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>METAR (current weather observation)</li>
                                <li>TAF (terminal aerodrome forecast)</li>
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-semibold text-slate-800 mb-1">What the AI Analyzes</h4>
                              <p className="mb-2"><strong>For METAR:</strong></p>
                              <ul className="list-disc list-inside space-y-1 ml-2 mb-2">
                                <li>Current flight category (VFR/MVFR/IFR/LIFR)</li>
                                <li>Visibility and cloud conditions</li>
                                <li>Wind (direction, speed, gusts)</li>
                                <li>Any significant weather phenomena</li>
                                <li>Temperature/dewpoint spread</li>
                              </ul>
                              <p className="mb-2"><strong>For TAF:</strong></p>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Expected changes over the forecast period</li>
                                <li>Timing of weather changes</li>
                                <li>Any temporary conditions (TEMPO) or probability forecasts (PROB)</li>
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-semibold text-slate-800 mb-1">Flow</h4>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>User enters ICAO code and clicks "Get Weather"</li>
                                <li>METAR/TAF data is fetched from aviationweather.gov API</li>
                                <li>User clicks "Generate Briefing" button</li>
                                <li>Frontend sends METAR/TAF to /api/weather/explain</li>
                                <li>Backend sends data to GPT-5.2 with a pilot-focused prompt</li>
                                <li>AI returns plain English explanation</li>
                                <li>Displayed under "AI Weather Briefing"</li>
                              </ol>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      {explanation ? (
                        <div className="explanation-container" data-testid="ai-explanation">
                          <ScrollArea className="h-auto max-h-96">
                            <div className="prose prose-sm max-w-none">
                              {explanation.split('\n').map((paragraph, idx) => (
                                <p key={idx} className="text-slate-700 leading-relaxed mb-3 last:mb-0">
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <p>Click "Generate Briefing" to get an AI-powered weather explanation</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Route Weather Tab */}
          <TabsContent value="route" data-testid="route-content">
            <RouteWeather 
              getFlightCategoryStyle={getFlightCategoryStyle}
              favorites={favorites}
            />
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" data-testid="history-content">
            <HistoryView 
              currentIcao={currentIcao}
              getFlightCategoryStyle={getFlightCategoryStyle}
            />
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites" data-testid="favorites-content">
            <FavoritesList
              favorites={favorites}
              onSelect={(icao) => {
                setSearchQuery(icao);
                searchWeather(icao);
                setActiveTab("briefing");
              }}
              onRemove={toggleFavorite}
              getFlightCategoryStyle={getFlightCategoryStyle}
            />
          </TabsContent>

          {/* Briefing Form Tab */}
          <TabsContent value="form" data-testid="form-content" forceMount className={activeTab !== 'form' ? 'hidden' : ''}>
            <BriefingForm
              metar={metar?.raw}
              taf={taf?.raw}
              explanation={explanation}
              icao={currentIcao}
              flightCategory={metar?.flight_category}
              isMultiAirport={isMultiAirport}
              multiAirportData={multiAirportData}
              editingBriefing={editingBriefing}
              onSaveSuccess={() => {
                setLibraryRefreshKey(prev => prev + 1);
                setEditingBriefing(null);
              }}
              onClearEdit={() => setEditingBriefing(null)}
            />
          </TabsContent>

          {/* Library Tab */}
          <TabsContent value="library" data-testid="library-content">
            <BriefingLibrary
              key={libraryRefreshKey}
              onLoadBriefing={(briefing) => {
                setEditingBriefing(briefing);
                // Only fetch weather if valid ICAO exists
                const briefingIcao = briefing.icao?.trim();
                if (briefingIcao && briefingIcao.length >= 4 && briefingIcao !== 'UNKNOWN') {
                  setSearchQuery(briefingIcao);
                  searchWeather(briefingIcao);
                } else {
                  // Clear search for route-only briefings
                  setSearchQuery('');
                }
                setActiveTab("form");
                toast.info(`Loaded briefing: ${briefing.title}`);
              }}
              onNewBriefing={() => {
                setEditingBriefing(null);
                setActiveTab("form");
              }}
              onDownloadBriefing={async (briefing, format) => {
                // Load the briefing into the form and switch to form tab for download
                setEditingBriefing(briefing);
                // Only fetch weather if valid ICAO exists
                const briefingIcao = briefing.icao?.trim();
                if (briefingIcao && briefingIcao.length >= 4 && briefingIcao !== 'UNKNOWN') {
                  setSearchQuery(briefingIcao);
                  await searchWeather(briefingIcao);
                } else {
                  // Clear weather data for route-only briefings
                  setSearchQuery('');
                  setCurrentIcao('');
                }
                setActiveTab("form");
                // Wait for form to render and assets to load (logo, map, etc.)
                await new Promise(resolve => setTimeout(resolve, 1500));
                // Trigger download via custom event
                window.dispatchEvent(new CustomEvent('triggerBriefingDownload', { detail: { format } }));
              }}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-auto bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleXduLike}
                className="text-slate-600 hover:text-indigo-600 hover:border-indigo-300 flex items-center gap-2"
              >
                <ThumbsUp className="w-4 h-4" />
                <span>+1 for XDU 🥳</span>
                <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-bold text-xs">{xduLikes}</span>
              </Button>
              
              {/* Install App Button - only show if not installed */}
              {!isAppInstalled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleInstallApp}
                  className="text-cyan-600 border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 flex items-center gap-2"
                >
                  <Smartphone className="w-4 h-4" />
                  <span className="hidden sm:inline">Install App</span>
                  <Download className="w-3 h-3" />
                </Button>
              )}
            </div>
            <span className="text-slate-400 text-xs">
              © Ravierr
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
